import React, { useState } from 'react';
import { useAuth } from '../context/auth-context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  User,
  Mail,
  Shield,
  Calendar,
  CreditCard,
  Settings,
  Key,
  Building,
  Bell,
  Globe,
  Check
} from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { getInitials } from '../lib/utils';
import { InsightsSubscriptions } from '../components/account/InsightsSubscriptions';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';

export default function Account() {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();

  if (!user) {
    return <div>Loading...</div>;
  }

  // Mock data for demonstration - replace with actual data from API
  const accountData = {
    plan: 'Free Tier',
    usage: {
      conversations: 7,
      dailyLimit: 10,
      pagesScanned: 15,
      pagesLimit: 20
    },
    billing: {
      status: 'active',
      nextBillingDate: null,
      paymentMethod: 'No payment method'
    },
    joinedDate: new Date('2024-01-15'), // Mock date
    lastLogin: new Date()
  };

  const usagePercentage = (accountData.usage.conversations / accountData.usage.dailyLimit) * 100;

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Account Settings</h1>
        <p className="text-muted-foreground">
          Manage your account information and preferences
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 max-w-[600px]">
          <TabsTrigger value="profile">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="preferences">
            <Settings className="w-4 h-4 mr-2" />
            Preferences
          </TabsTrigger>
          <TabsTrigger value="subscriptions">
            <Bell className="w-4 h-4 mr-2" />
            Email Subscriptions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <div className="grid gap-6">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-6">
              <Avatar className="h-24 w-24">
                {user.image && <AvatarImage src={user.image} alt={user.name || 'User'} />}
                <AvatarFallback className="text-2xl">
                  {user.name ? getInitials(user.name) : 'U'}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{user.name || 'Not provided'}</p>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3" /> Email
                  </p>
                  <p className="font-medium">{user.email}</p>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Shield className="h-3 w-3" /> Role
                  </p>
                  <Badge variant={user.role === 'super_admin' ? 'default' : 'secondary'}>
                    {user.role === 'super_admin' ? 'Super Admin' : 'User'}
                  </Badge>
                </div>
                
                <div className="pt-2 space-y-1">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Member since
                  </p>
                  <p className="text-sm">{format(accountData.joinedDate, 'MMMM d, yyyy')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription & Usage */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Subscription & Usage
            </CardTitle>
            <CardDescription>
              Your current plan and usage statistics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{accountData.plan}</p>
                <p className="text-sm text-muted-foreground">
                  {accountData.billing.status === 'active' ? 'Active subscription' : 'No active subscription'}
                </p>
              </div>
              <Link to="/account/licensing">
                <Button>Upgrade Plan</Button>
              </Link>
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Daily Conversations</p>
                  <p className="text-sm text-muted-foreground">
                    {accountData.usage.conversations} / {accountData.usage.dailyLimit}
                  </p>
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${usagePercentage}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Crawled Pages</p>
                  <p className="text-sm text-muted-foreground">
                    {accountData.usage.pagesScanned} / {accountData.usage.pagesLimit}
                  </p>
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${(accountData.usage.pagesScanned / accountData.usage.pagesLimit) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Your free tier includes basic chatbot and voice bot features with limited daily conversations.
                <Link to="/account/licensing" className="text-primary hover:underline ml-1">
                  View all plans
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Security
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Authentication Method</p>
                <p className="text-sm text-muted-foreground">Google OAuth</p>
              </div>
              <Button variant="outline" size="sm" disabled>
                <Settings className="h-4 w-4 mr-2" />
                Configure
              </Button>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Last Login</p>
                <p className="text-sm text-muted-foreground">
                  {format(accountData.lastLogin, 'MMMM d, yyyy \'at\' h:mm a')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer Info (if applicable) */}
        {user.customer && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Organization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium">{user.customer}</p>
              {user.allowedSites && user.allowedSites.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground mb-2">Allowed Sites</p>
                  <div className="space-y-1">
                    {user.allowedSites.map((site, index) => (
                      <Badge key={index} variant="secondary">
                        {site}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
          </div>
        </TabsContent>

        <TabsContent value="preferences" className="space-y-6">
          {/* Language Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Display Language
              </CardTitle>
              <CardDescription>
                Choose your preferred language for viewing content. AI-generated content will be translated to your selected language.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`
                      flex items-center gap-3 p-4 rounded-lg border-2 transition-all
                      ${language === lang.code
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }
                    `}
                  >
                    <span className="text-2xl" role="img" aria-label={lang.name}>
                      {lang.flag}
                    </span>
                    <div className="flex-1 text-left">
                      <p className="font-medium">{lang.name}</p>
                      <p className="text-sm text-muted-foreground">{lang.native}</p>
                    </div>
                    {language === lang.code && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Note: Content is originally generated in English for best AI quality, then translated on-demand to your selected language.
                Translations are cached for faster loading on subsequent views.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscriptions">
          <InsightsSubscriptions />
        </TabsContent>
      </Tabs>
    </div>
  );
}